import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { formatAuPhoneForDisplay, normalizeAuPhone } from "../../lib/phone.js";
import { Button, Field, Input } from "../../lib/ui.js";
import { SectionCard, errorStyle, linkButtonStyle, mutedStyle, tdStyle, thStyle } from "./common.js";

interface CallRouteRow {
  id: string;
  route_name: string;
  destination_e164: string;
  enabled: boolean;
}

/** Where the receptionist transfers a caller ("put me through to the manager"). */
export function CallRoutesSection() {
  const { activeBusinessId } = useAuth();
  const [callRoutes, setCallRoutes] = useState<CallRouteRow[]>([]);
  const [businessState, setBusinessState] = useState<string | null>(null);
  const [routeName, setRouteName] = useState("");
  const [routeDest, setRouteDest] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!activeBusinessId) return;
    const [{ data: routes }, { data: business }] = await Promise.all([
      supabase.from("call_routes").select("id, route_name, destination_e164, enabled").eq("business_id", activeBusinessId).order("route_name"),
      supabase.from("businesses").select("state").eq("id", activeBusinessId).single(),
    ]);
    setCallRoutes((routes ?? []) as CallRouteRow[]);
    setBusinessState(business?.state ?? null);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  async function handleAddRoute(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    const normalized = normalizeAuPhone(routeDest, businessState);
    if (!normalized.ok) {
      setError(normalized.error);
      return;
    }
    const { error: err } = await supabase.from("call_routes").insert({
      business_id: activeBusinessId,
      route_name: routeName,
      destination_e164: normalized.e164,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setRouteName("");
    setRouteDest("");
    await load();
  }

  async function toggleRouteEnabled(row: CallRouteRow) {
    await supabase.from("call_routes").update({ enabled: !row.enabled }).eq("id", row.id);
    await load();
  }

  async function handleDeleteRoute(id: string) {
    await supabase.from("call_routes").delete().eq("id", id);
    await load();
  }

  return (
    <SectionCard title="Call routes" description={'Where the receptionist transfers a caller for things like "put me through to the manager". A number entered without an area code uses the state in your address below.'}>
      <form onSubmit={handleAddRoute} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Route name">
          <Input required value={routeName} onChange={(e) => setRouteName(e.target.value)} placeholder="manager" style={{ width: 170 }} />
        </Field>
        <Field label="Destination number">
          <Input required value={routeDest} onChange={(e) => setRouteDest(e.target.value)} style={{ width: 240 }} />
        </Field>
        <Button type="submit" variant="primary">
          Add
        </Button>
      </form>
      {error && <p style={{ ...errorStyle, marginBottom: 10 }}>{error}</p>}
      <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle}>Route</th>
            <th style={thStyle}>Destination</th>
            <th style={thStyle}>Enabled</th>
            <th style={thStyle} />
          </tr>
        </thead>
        <tbody>
          {callRoutes.map((r) => (
            <tr key={r.id}>
              <td style={tdStyle}>{r.route_name}</td>
              <td style={tdStyle}>{formatAuPhoneForDisplay(r.destination_e164)}</td>
              <td style={tdStyle}>
                <button onClick={() => void toggleRouteEnabled(r)} style={linkButtonStyle}>
                  {r.enabled ? "Yes (disable)" : "No (enable)"}
                </button>
              </td>
              <td style={tdStyle}>
                <button onClick={() => void handleDeleteRoute(r.id)} style={{ ...linkButtonStyle, color: "var(--rc-danger)" }}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {callRoutes.length === 0 && (
            <tr>
              <td colSpan={4} style={{ ...tdStyle, ...mutedStyle }}>
                No call routes yet.
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
    </SectionCard>
  );
}
